import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'

// ─── Domain Types ─────────────────────────────────────────────────────────────

export interface Promotion {
  id: number
  account_id: number
  kind: string
  value_type: 'fixed' | 'percent'
  value: number
  currency: string
  funding: string
  starts_at: string | null
  ends_at: string | null
  max_redemptions: number | null
  per_customer_limit: number | null
  conditions: Record<string, unknown>
  status: string
  redemptions_count: number
  created_at: string
  updated_at: string
}

export interface PromotionRedemption {
  id: number
  customer_id: number | null
  lease_id: number | null
  invoice_id: number | null
  amount: number
  applied_amount: number
  status: string
  redeemed_at: string | null
}

export interface PromotionDetail extends Promotion {
  redemptions: PromotionRedemption[]
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const promotionKeys = {
  all: ['promotions'] as const,
  lists: () => [...promotionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...promotionKeys.lists(), filters] as const,
  details: () => [...promotionKeys.all, 'detail'] as const,
  detail: (id: number) => [...promotionKeys.details(), id] as const,
  redemptions: (id: number) => [...promotionKeys.detail(id), 'redemptions'] as const,
}

// ─── Filters ──────────────────────────────────────────────────────────────────

export interface PromotionFilters {
  page?: number
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function usePromotions(page = 1, filters: PromotionFilters = {}) {
  return useQuery({
    queryKey: promotionKeys.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      const { data } = await api.get<ApiResponse<Promotion[]>>('/api/promotions', { params })
      return data
    },
  })
}

// EP-116 T028: a usePromotionDetail(id) hook used to live here — no consumer
// ever called it. PromotionsPage never drills into a single promotion by id:
// the list query already carries every field the page renders, and per-
// promotion redemption history is served by usePromotionRedemptions (below,
// which IS wired). Removed 2026-08 (T028).

export function usePromotionRedemptions(id: number) {
  return useQuery({
    queryKey: promotionKeys.redemptions(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<PromotionDetail>>(
        `/api/promotions/${id}/redemptions`,
      )
      return data.data
    },
    enabled: !!id,
  })
}

interface CreatePromotionInput {
  kind: string
  value_type: 'fixed' | 'percent'
  value: number
  currency: string
  funding: string
  starts_at?: string | null
  ends_at?: string | null
  max_redemptions?: number | null
  per_customer_limit?: number | null
  status?: string
  conditions?: Record<string, unknown>
}

export function useCreatePromotion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreatePromotionInput) => {
      const { data } = await api.post<ApiResponse<Promotion>>('/api/promotions', {
        promotion: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, promotionKeys.lists())
    },
  })
}

// EP-116 T028: a useUpdatePromotion() hook used to live here — no consumer
// ever called it. PromotionsPage's interaction surface is create + delete
// only; status transitions (e.g. active → paused) are backend-automated by
// the cap/forfeit engine, not manually edited from the UI. No edit affordance
// exists anywhere, and none is noted as deferred in TODO.md/CLAUDE.md.
// Removed 2026-08 (T028).

export function useDeletePromotion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/promotions/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, promotionKeys.lists())
    },
  })
}
